import React, { useState, useCallback } from 'react'
import {
  FileText,
  CheckCircle,
  Printer,
  Award,
  ShieldCheck,
  TrendingDown,
  DollarSign,
  Calendar,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import { HubShell } from './HubShell'
import { Form8824InlinePanel } from '../forms/Form8824Preview'
import { useExchangeHubStatus } from '../../hooks/useExchangeHubStatus'
import toast from 'react-hot-toast'

interface IrsHubProps {
  onClose: () => void
  onSectionComplete?: () => void
}

const REVIEW_KEY = 'irs_hub_8824_reviewed'

function loadReviewed(): boolean {
  return localStorage.getItem(REVIEW_KEY) === 'true'
}

export const IrsHub: React.FC<IrsHubProps> = ({ onClose, onSectionComplete }) => {
  const { isHubSubmitted, updateStatus } = useExchangeHubStatus()
  const [form8824Open, setForm8824Open] = useState(false)
  const [reviewed, setReviewed] = useState(loadReviewed)
  const submitted = isHubSubmitted('irs')

  const handleReviewToggle = useCallback((checked: boolean) => {
    setReviewed(checked)
    localStorage.setItem(REVIEW_KEY, String(checked))
  }, [])

  const handleSubmit = useCallback(async () => {
    if (!reviewed && !submitted) {
      toast.error('Please review and acknowledge the Form 8824 before completing.')
      return
    }
    localStorage.setItem('irs_hub_submitted', 'true')
    localStorage.setItem('irs_hub_submitted_date', new Date().toISOString())
    await updateStatus({})
    window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
    toast.success('IRS Hub complete — exchange wrap-up recorded.')
    onSectionComplete?.()
    onClose()
  }, [reviewed, submitted, updateStatus, onSectionComplete, onClose])

  const completionPercent = submitted ? 100 : reviewed ? 80 : 0

  return (
    <HubShell
      icon={FileText}
      title="IRS Hub — Tax Reporting & Wrap-Up"
      subtitle="Review your Form 8824 and complete your §1031 exchange record."
      completionPercent={completionPercent}
      isSubmitted={submitted}
      color="cyan"
      onClose={onClose}
      canSubmit={reviewed || submitted}
      completedMessage="Exchange wrap-up complete"
      nextHubMessage="Your §1031 exchange is fully documented."
      onSubmit={handleSubmit}
      submitLabel="Complete Exchange"
      submitIcon={Award}
      submitDisabledReason="Review and acknowledge your Form 8824 to complete."
      showSaveIndicator={false}
    >
      <div className="space-y-6 max-w-2xl mx-auto pb-4">
        <div className="rounded-xl border border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-900/20 p-5 flex items-start gap-4">
          <div className="p-2.5 rounded-full bg-cyan-100 dark:bg-cyan-800/40 flex-shrink-0">
            <ShieldCheck className="h-6 w-6 text-cyan-600 dark:text-cyan-400" aria-hidden="true" />
          </div>
          <div>
            <p className="text-sm font-semibold text-cyan-900 dark:text-cyan-100 mb-1">
              Your §1031 exchange is complete
            </p>
            <p className="text-sm text-cyan-800 dark:text-cyan-200/90 leading-relaxed">
              Open1031 has orchestrated every phase of your like-kind exchange. Review your
              auto-populated IRS Form 8824 below, then acknowledge to close the record.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-lg border border-border bg-card p-4 flex flex-col gap-1.5">
            <div className="flex items-center gap-2 text-cyan-600 dark:text-cyan-400">
              <TrendingDown className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wide">Deferred</span>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              Capital gains tax deferred via §1031 like-kind exchange
            </p>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 flex flex-col gap-1.5">
            <div className="flex items-center gap-2 text-cyan-600 dark:text-cyan-400">
              <Calendar className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wide">Deadlines Met</span>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              45-day identification and 180-day acquisition rules satisfied
            </p>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 flex flex-col gap-1.5">
            <div className="flex items-center gap-2 text-cyan-600 dark:text-cyan-400">
              <DollarSign className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wide">New Basis Set</span>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              Replacement property basis calculated per IRC §1031 rules
            </p>
          </div>
        </div>

        <div className="rounded-xl border-2 border-cyan-200 dark:border-cyan-800 bg-white dark:bg-slate-800 overflow-hidden">
          <div className="px-5 py-4 border-b border-cyan-100 dark:border-cyan-900 bg-cyan-50/60 dark:bg-cyan-900/10 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-cyan-100 dark:bg-cyan-800/40 flex items-center justify-center flex-shrink-0">
                <FileText className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">IRS Form 8824</p>
                <p className="text-xs text-muted-foreground">
                  Like-Kind Exchanges — auto-populated from your exchange data
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setForm8824Open((prev) => !prev)}
              aria-expanded={form8824Open}
              aria-controls="form-8824-preview"
              className="inline-flex items-center justify-center gap-1.5 rounded-md text-sm font-medium px-4 py-2
                bg-cyan-600 hover:bg-cyan-700 dark:bg-cyan-600 dark:hover:bg-cyan-700 text-white transition-colors"
            >
              <FileText className="h-3.5 w-3.5" />
              {form8824Open ? 'Hide Form 8824' : 'View Form 8824'}
              {form8824Open
                ? <ChevronUp className="h-3.5 w-3.5" />
                : <ChevronDown className="h-3.5 w-3.5" />
              }
            </button>
          </div>
          <div className="px-5 py-4 space-y-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <CheckCircle className="h-3.5 w-3.5 text-green-500" />
              <span>Exchanger name, EIN, and relinquished property details — from Sales Hub</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <CheckCircle className="h-3.5 w-3.5 text-green-500" />
              <span>Identified replacement properties and dates — from Identification Hub</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <CheckCircle className="h-3.5 w-3.5 text-green-500" />
              <span>Realized gain, recognized gain, deferred gain, and new basis — from Calculator</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <CheckCircle className="h-3.5 w-3.5 text-green-500" />
              <span>Replacement property FMV, closing date, and seller info — from Purchase Hub</span>
            </div>
          </div>
        </div>

        {form8824Open && (
          <div id="form-8824-preview">
            <Form8824InlinePanel onClose={() => setForm8824Open(false)} />
          </div>
        )}

        <div
          className={`rounded-lg border-2 transition-all duration-300 ${
            submitted
              ? 'border-cyan-400 dark:border-cyan-600 bg-cyan-50/80 dark:bg-cyan-900/30'
              : reviewed
              ? 'border-cyan-400 dark:border-cyan-600 bg-cyan-50/80 dark:bg-cyan-900/30'
              : 'border-cyan-200 dark:border-cyan-900 bg-cyan-50/60 dark:bg-cyan-900/20'
          }`}
        >
          <label
            htmlFor="irs-hub-reviewed"
            className={`flex items-center justify-between gap-4 p-4 ${submitted ? 'cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span className="text-sm font-medium text-cyan-900 dark:text-cyan-200">
              I have reviewed the Form 8824 data and confirm it accurately reflects my §1031 exchange.
            </span>
            <div className="relative flex-shrink-0">
              <input
                type="checkbox"
                id="irs-hub-reviewed"
                checked={reviewed || submitted}
                onChange={(e) => !submitted && handleReviewToggle(e.target.checked)}
                disabled={submitted}
                className="sr-only peer"
                aria-label="Acknowledge Form 8824 review"
              />
              <div className="w-11 h-6 rounded-full bg-slate-300 dark:bg-slate-600 peer-checked:bg-cyan-600 transition-colors" />
              <div className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-5" />
            </div>
          </label>
        </div>

        {submitted && (
          <div className="rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-5 flex items-center gap-4">
            <Award className="h-7 w-7 text-green-600 dark:text-green-400 flex-shrink-0" aria-hidden="true" />
            <div>
              <p className="text-sm font-semibold text-green-900 dark:text-green-100">
                §1031 Exchange fully documented
              </p>
              <p className="text-sm text-green-800 dark:text-green-200/90">
                All hubs are complete. Your Form 8824 data is preserved for tax filing.
                Consult your tax advisor before filing.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setForm8824Open((prev) => !prev)}
              className="ml-auto flex-shrink-0 inline-flex items-center gap-1.5 rounded-md text-sm font-medium px-3 py-1.5
                border border-green-300 dark:border-green-700 text-green-800 dark:text-green-200
                hover:bg-green-100 dark:hover:bg-green-800/40 transition-colors"
            >
              <Printer className="h-3.5 w-3.5" />
              Print
            </button>
          </div>
        )}
      </div>
    </HubShell>
  )
}
